// Single axios instance used by every component for projector calls.
//
// Token injection lives in a request interceptor that reads from a
// caller-supplied getter — set via setTokenGetter() once Privy boots.
// This decouples the API client from the auth library so tests can
// drive it without React.
//
// The 401 path runs the getter again (forcing a refresh) and retries
// once. Two attempts max — beyond that, the user needs to log back in.

import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from './env';

type TokenGetter = () => Promise<string | null>;

let tokenGetter: TokenGetter = async () => null;

export function setTokenGetter(fn: TokenGetter): void {
  tokenGetter = fn;
}

export const api: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  // YAML bodies for /dossier/* — let the caller decide how to parse.
  responseType: 'text',
  // Required so dev-only DEV_AUTH_BYPASS path still goes through the
  // same flight — interceptor decides whether to attach a bearer.
  validateStatus: (s) => s < 500,
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Dev bypass mirrors projector behavior: when the local server is
  // running with DEV_AUTH_BYPASS=true, we skip token attachment so a
  // dev with no Privy app id can still drive the UI.
  if (env.devAuthBypass) return config;

  const token = await tokenGetter();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// 401 retry — re-resolve the token (Privy auto-refreshes) and replay
// the request once. A flag on the config prevents infinite loops if
// the refreshed token is still rejected.
api.interceptors.response.use(undefined, async (error: AxiosError) => {
  if (!error.response || error.response.status !== 401) throw error;
  const original = error.config as
    | (InternalAxiosRequestConfig & { _retry?: boolean })
    | undefined;
  if (!original || original._retry) throw error;
  if (env.devAuthBypass) throw error;

  original._retry = true;
  const fresh = await tokenGetter();
  if (fresh) original.headers.set('Authorization', `Bearer ${fresh}`);
  return api.request(original);
});
